import * as React from 'react';

import { useLockFn } from 'ahooks';
import { Flex, Form, notification } from 'antd';
import { useNavigate } from 'react-router-dom';

import { PrimitiveForm } from 'components';
import { useDiscountCodeValue } from 'context';
import { createDiscountCode, updateDiscountCode } from 'services';
import type { SelectOption, TreeSelectSource } from 'types';
import { fixedParseFloat, normalizeToString, PATHNAME } from 'utils';

import { DiscountCodeFormAction } from './action';
import { DiscountCodeFormContent } from './content';

const {
  LOCAL: {
    DISCOUNT_CODES: { BASE_URL: DISCOUNT_CODES_BASE_URL },
  },
} = PATHNAME;

type FieldType = Pick<
  ReturnType<typeof useDiscountCodeValue>,
  'code' | 'minCartPrice' | 'maxDiscountPrice' | 'isOnlyForFirstTime' | 'enable'
> &
  Partial<
    Record<'products' | 'brands', SelectOption[]> &
      Record<'categories', TreeSelectSource[]>
  >;

const DiscountCodeForm = () => {
  const [form] = Form.useForm<FieldType>();
  const {
    id = '',
    type,
    amount,
    duration,
    defaultCode,
    defaultEnable,
    mode,
  } = useDiscountCodeValue();
  const navigate = useNavigate();

  const formattedAmount = React.useMemo<number | undefined>(() => {
    const isPercentageType = type === 'Percentage';
    const isNumberAmount = typeof amount === 'number';

    if (!isNumberAmount) return;

    const fixedAmount = fixedParseFloat(amount, 3);
    const amountValue = Math.min(Math.max(0, fixedAmount), 100);

    if (!isPercentageType) return fixedAmount;

    return amountValue;
  }, [type, amount]);

  const onFinish = useLockFn(
    async ({
      code = '',
      minCartPrice,
      maxDiscountPrice,
      isOnlyForFirstTime = false,
      enable = defaultEnable!,
      products = [],
      brands = [],
      categories = [],
    }: FieldType) => {
      const isUpdatedMode = mode === 'Update';

      const params: Parameters<typeof updateDiscountCode>[0] = {
        id,
      };
      const body: Parameters<typeof updateDiscountCode>[1] = {
        code,
        enable,
        minCartPrice,
        maxDiscountPrice,
        isOnlyForFirstTime,
        type: type!,
        duration: duration!,
        amount: formattedAmount || 0,
        productsIds: products.map(({ value }) => normalizeToString(value)),
        brandsIds: brands.map(({ value }) => normalizeToString(value)),
        categoriesIds: categories.map(({ value }) => normalizeToString(value)),
      };

      (isUpdatedMode
        ? updateDiscountCode(params, body)
        : createDiscountCode(body)
      )
        .then(() => {
          notification.success({
            message: `کد تخفیف ${isUpdatedMode ? defaultCode : code} با موفقیت ${isUpdatedMode ? 'ویرایش' : 'ساخته'} شد.`,
          });
          navigate(DISCOUNT_CODES_BASE_URL);
        })
        .catch(message => {
          notification.error({ message });
        });
    },
  );

  return (
    <PrimitiveForm
      form={form}
      fields={[
        {
          name: 'amount',
          value: formattedAmount,
        },
      ]}
      onFinish={onFinish}
    >
      <Flex
        className='*:rounded *:bg-white max-xl:flex-col max-xl:last:*:sticky max-xl:last:*:inset-x-0 max-xl:last:*:bottom-0 max-xl:last:*:z-10 max-xl:last:*:shadow-inner xl:items-start xl:first:*:basis-[70%] xl:last:*:basis-[30%]'
        gap='large'
      >
        <DiscountCodeFormContent />
        <DiscountCodeFormAction />
      </Flex>
    </PrimitiveForm>
  );
};

export { DiscountCodeForm };
